import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { AdminContext } from './AdminContext';
import { DatabaseContext } from '../database/DatabaseHandler';
import IParamScript from '../database/interfaces/paramScript/IParamScript';
import PrestaGlobals from '../common/PrestaGlobals';
import AdminApi from './AdminApi';
import MarginInput from './MarginInput';
import { isObjectValue, valueToText, textToValue, valueToStored, formatParamRef } from './paramUtils';

/**
 * Live editor for ALL style parameters of the current product — including the ones intentionally
 * not exposed as customer cards (e.g. ShelfZone margins). Editing a value and applying pushes the
 * full paramScripts via DatabaseContext.updateScripts, which re-derives `parameters` and re-fires
 * SetStyleScript → the 3D model updates live. No DB persistence yet (M3).
 */
const StyleParamsEditor: React.FC = () => {
    const data = React.useContext(DatabaseContext);
    const admin = React.useContext(AdminContext);
    const params = data.paramScripts ?? [];

    // Raw input text per param id (only set for touched fields). Object fields also track a parse error.
    const [text, setText] = React.useState<Record<string, string>>({});
    const [errors, setErrors] = React.useState<Record<string, string | null>>({});
    const [status, setStatus] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);

    // Discard local edits when the upstream paramScripts change (apply, product switch, reload).
    React.useEffect(() => {
        setText({});
        setErrors({});
    }, [data.paramScripts]);

    const dirty = Object.keys(text).length > 0;
    const hasError = Object.values(errors).some(Boolean);

    const onChange = (p: IParamScript, next: string) => {
        setText(prev => ({ ...prev, [p.id]: next }));
        if (isObjectValue(p.parameterValue)) {
            try {
                JSON.parse(next);
                setErrors(prev => ({ ...prev, [p.id]: null }));
            } catch (e: any) {
                setErrors(prev => ({ ...prev, [p.id]: e?.message ?? 'Érvénytelen JSON' }));
            }
        }
    };

    const handleApply = () => {
        const base = data.paramScripts ?? [];
        const next = base.map((p) => {
            if (!(p.id in text)) return p;
            return { ...p, parameterValue: textToValue(p.parameterValue, text[p.id]) };
        });
        // Same uconScript reference → only SetStyleScript re-fires (not SetUConScript).
        data.updateScripts(next, data.uconScript);
        setText({});
        setErrors({});
    };

    // Persist edited values as the current shop's defaults (uc_style_parameter_values), then reload.
    const handleSave = async () => {
        const base = data.paramScripts ?? [];
        const edited = base.filter(p => p.id in text);
        setSaving(true);
        let ok = 0;
        for (const p of edited) {
            const value = valueToStored(textToValue(p.parameterValue, text[p.id]));
            const res = await AdminApi.saveParamValue(p.id, PrestaGlobals.productId, value);
            if (res && res.success) ok++;
        }
        setSaving(false);
        setStatus(`Mentve ${ok}/${edited.length} érték a bolthoz (#${PrestaGlobals.shopId}).`);
        setText({});
        setErrors({});
        data.reloadScripts();
    };

    const handleDelete = async (p: IParamScript) => {
        const label = p.typeDescriptor?.label || p.parameterName;
        if (!window.confirm(`Biztosan törli a(z) „${label}” paramétert? Ez a műveletet nem vonható vissza.`)) return;
        setSaving(true);
        const res = await AdminApi.deleteParam(p.id, PrestaGlobals.productId);
        setSaving(false);
        setStatus(res && res.success ? `Törölve: „${label}”` : 'Törlés sikertelen.');
        data.reloadScripts();
    };

    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Button variant="contained" size="small" disabled={!dirty || hasError} onClick={handleApply}>
                    Alkalmaz (élő előnézet)
                </Button>
                <Button variant="outlined" size="small" color="primary" disabled={!dirty || hasError || saving} onClick={handleSave}>
                    {saving ? 'Mentés…' : 'Mentés (bolthoz)'}
                </Button>
                <Button variant="text" size="small" disabled={!dirty} onClick={() => { setText({}); setErrors({}); }}>
                    Visszaállítás
                </Button>
            </Stack>
            {status && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{status}</Typography>
            )}
            <Typography variant="caption" color={hasError ? 'error' : 'text.secondary'} sx={{ display: 'block', mb: 1 }}>
                {hasError
                    ? 'Hibás JSON érték — javítsa az alkalmazás előtt.'
                    : (dirty
                        ? 'Nem mentett változás — élő előnézet a Unity-ban, az adatbázisba nem mentve.'
                        : 'Minden paraméter (a vásárlónak nem megjelenített, „rejtett” értékek is) itt szerkeszthető.')}
            </Typography>

            <Divider sx={{ my: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                    Meglévő paraméterek ({params.length})
                </Typography>
            </Divider>

            {params.length === 0 && (
                <Typography variant="body2" color="text.secondary">— Nincs paraméter —</Typography>
            )}

            {params.map((p) => {
                const value = text[p.id] ?? valueToText(p.parameterValue);
                const obj = isObjectValue(p.parameterValue);
                const err = errors[p.id];
                return (
                    <Box key={p.id} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexGrow: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2">
                                    {p.typeDescriptor?.label || p.parameterName}
                                </Typography>
                                {p.parameterType && <Chip size="small" label={p.parameterType} variant="outlined" />}
                                {p.typeDescriptor?.hidden && <Chip size="small" color="warning" label="Rejtett" />}
                            </Box>
                            <Tooltip title="Definíció szerkesztése">
                                <IconButton size="small" onClick={() => admin.openDescriptorEditor(p)}
                                    sx={{ color: 'primary.main' }}>
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Paraméter törlése">
                                <IconButton size="small" disabled={saving}
                                    onClick={() => handleDelete(p)} sx={{ color: 'error.main' }}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {formatParamRef(p)}
                        </Typography>
                        {p.parameterType === 'Margin'
                            ? <MarginInput value={value} onChange={(next) => onChange(p, next)} />
                            : <TextField
                                value={value}
                                onChange={(e) => onChange(p, e.target.value)}
                                size="small"
                                fullWidth
                                multiline={obj}
                                minRows={obj ? 4 : undefined}
                                error={!!err}
                                helperText={err ? `JSON hiba: ${err}` : undefined}
                                spellCheck={false}
                                sx={obj ? { '& textarea': { fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre' } } : undefined}
                            />}
                    </Box>
                );
            })}
        </Box>
    );
};

export default StyleParamsEditor;
